import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import { Immutable } from "immer";
import React, { type JSX, useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";

import { constants } from "@reactivated/constants";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { RichText } from "../../../components/RichText";
import { IProduct } from "../../../models/catalogue";
import { WebPageURL, isoSafeHTML } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { usePageSetting } from "../../../utils/settings";
import { supTrademarks, supTrademarksText } from "../../../utils/supTrademarks";
import { ConfiguratorPanel } from "../ConfiguratorPanel";
import { ConfiguratorPanelTrigger } from "../ConfiguratorPanelTrigger";
import { PanelRootProductSelectorOption } from "../PanelRootProductSelectorOption";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { defaults } from "../defaults";
import { IModularConfiguratorOptionSet } from "../models";
import { setSelectedRootProduct } from "../reducers";
import { offeredRootProductsSelector, rootProductSelector } from "../selectors";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

const allowBodyScrollLockTouchMove = (el: HTMLElement | Element): boolean => {
    const document = getDocument();
    while (el && document && el !== document.body) {
        if (el.getAttribute("data-body-scroll-lock-ignore") !== null) {
            return true;
        }
        el = el.parentElement as HTMLElement | Element;
    }
    return false;
};

const buildPanelTriggerContent = (
    selectedRootProduct: IProduct | null,
    appSlug: string | null | undefined,
): React.ReactNode => {
    if (!selectedRootProduct) {
        return null;
    }
    if (selectedRootProduct.attributes.configurator_panel_trigger_title) {
        return (
            <RichText
                inline={true}
                html={supTrademarks(
                    isoSafeHTML.wrap(
                        `${selectedRootProduct.attributes.configurator_panel_trigger_title.value}`,
                    ),
                    appSlug,
                )}
            />
        );
    }
    if (selectedRootProduct.description) {
        // TODO(i18n): Don't assume word order or punctuation. Use jt or complete translated sentence: jt`${description} (${title})`
        return (
            <>
                <RichText
                    inline={true}
                    html={supTrademarks(
                        selectedRootProduct.description,
                        appSlug,
                    )}
                />
                (
                {supTrademarksText(
                    selectedRootProduct.title.replace("TEMPUR-", ""),
                    appSlug,
                )}
                )
            </>
        );
    }
    return supTrademarksText(selectedRootProduct.title, appSlug);
};

export const PanelRootProductSelector = (
    props: Immutable<{
        optionSet: IModularConfiguratorOptionSet;
        chatLinkComponent?: JSX.Element | null;
        liveChatHeader?: string;
        financingLink?: WebPageURL;
    }>,
): React.ReactNode => {
    const appSlug = usePageSetting("app_slug") || "";
    const panelContainerRef = useRef<HTMLDivElement>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    const dispatch = useAppDispatch();
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const rootProducts = state.entities.rootProducts;
    const offeredProducts = offeredRootProductsSelector(state);
    const selectedRootProduct = rootProductSelector(state);

    useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const onClosePanel = (): void => {
        // `enableBodyScroll` should run before setState `isPanelOpen: false`,
        // so inline style `position: fixed` from `<body>` will be removed first before removing `.configurator-panel-open` from `<body>`.
        // This way can avoid the issue of the page jumping to the top when panel is opened/closed
        enableBodyScroll(panelContainerRef.current as HTMLElement);
        setIsPanelOpen(false);
        // Wait a few seconds to remove inline style `position: fixed` before removing `.configurator-panel-open` from `<body>`.
        setTimeout(() => {
            const document = getDocument();
            if (document) {
                document.body.classList.remove("configurator-panel-open");
            }
        }, 500);
    };

    const onOpenPanel = (): void => {
        setIsPanelOpen(true);
        setTimeout(() => {
            const window = getWindow();
            if (
                (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE
            ) {
                disableBodyScroll(panelContainerRef.current as HTMLElement, {
                    allowTouchMove: allowBodyScrollLockTouchMove,
                });
            } else {
                enableBodyScroll(panelContainerRef.current as HTMLElement);
            }
        }, 0);
    };

    const onSelectRootProduct = (product: IProduct): void => {
        dispatch(setSelectedRootProduct(product));
    };

    if (rootProducts.length <= 1 || !selectedRootProduct) {
        return null;
    }

    return (
        <>
            <CSSTransition
                in={isPanelOpen}
                timeout={500}
                classNames="configurator__panel-"
                unmountOnExit={true}
                nodeRef={panelContainerRef}
            >
                <div ref={panelContainerRef}>
                    <ConfiguratorPanel
                        prefixNameCapped={
                            props.optionSet.root_product_selector.label
                        }
                        onClosePanel={onClosePanel}
                        closeOnOptionActivation={true}
                        panelClass={"model"}
                        chatLinkComponent={props.chatLinkComponent}
                        liveChatHeader={props.liveChatHeader}
                        enableChat={props.optionSet.layout.enable_chat}
                        radioSelector={
                            <div className="configurator__radio-container">
                                <fieldset>
                                    <legend className="ada-screenreader-only">
                                        {
                                            props.optionSet
                                                .root_product_selector.label
                                        }
                                    </legend>
                                    <ul className={styles.container}>
                                        {offeredProducts.map((rootProduct) => (
                                            <PanelRootProductSelectorOption
                                                key={`${rootProduct.id}`}
                                                optionSet={props.optionSet}
                                                rootProduct={rootProduct}
                                                selectedRootProduct={
                                                    selectedRootProduct
                                                }
                                                enableRatings={
                                                    appSlug !==
                                                        AppSlug.TEMPURCA &&
                                                    props.optionSet
                                                        .root_product_selector
                                                        .enable_ratings
                                                }
                                                onSelect={() => {
                                                    onSelectRootProduct(
                                                        rootProduct,
                                                    );
                                                }}
                                                onClosePanel={onClosePanel}
                                            />
                                        ))}
                                    </ul>
                                </fieldset>
                            </div>
                        }
                    />
                </div>
            </CSSTransition>
            <ConfiguratorPanelTrigger
                prefixNameCapped={props.optionSet.root_product_selector.label}
                onOpenPanel={onOpenPanel}
            >
                {buildPanelTriggerContent(selectedRootProduct, appSlug)}
            </ConfiguratorPanelTrigger>
        </>
    );
};
